/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
// Address autocomplete uses AWS GeoPlaces via /api/estimate/autocomplete
import api from '../utils/api';
import { etToISO } from '../utils/et';

function AddressInput({ label, value, onChange, onPlaceSelect, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (val) => {
    onChange(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (val.length < 3) { setSuggestions([]); setShowDropdown(false); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/estimate/autocomplete?q=' + encodeURIComponent(val));
        setSuggestions(res.data || []);
        setShowDropdown((res.data || []).length > 0);
      } catch (e) { setSuggestions([]); }
    }, 250);
  };

  const handleSelect = (label) => {
    onPlaceSelect(label);
    setSuggestions([]);
    setShowDropdown(false);
  };

  return (
    <div style={{ marginBottom: 12, position: 'relative' }} ref={wrapperRef}>
      <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{label}</label>
      <input value={value} onChange={e => handleChange(e.target.value)}
        onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
        placeholder={placeholder} style={{ width: '100%' }} />
      {showDropdown && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white',
          border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 1000, maxHeight: 200, overflowY: 'auto' }}>
          {suggestions.map((s, i) => (
            <div key={i} onClick={() => handleSelect(s.label)}
              style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none' }}
              onMouseEnter={e => e.target.style.background = '#f5f5f3'}
              onMouseLeave={e => e.target.style.background = 'white'}>
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Estimate() {
  const [form, setForm] = useState({
    originAddress: '',
    destinationAddress: '',
    vehicleType: 'wheelchair_van',
    numTransporters: 1,
    roundTrip: false,
    driverStays: false,
    waitMinutes: 60,
    holidayMultiplier: 1,
    departureTime: '',
    timeUnknown: false,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEstimate = async () => {
    if (!form.originAddress || !form.destinationAddress) {
      setError('Enter both pickup and destination addresses.');
      return;
    }
    // Erika 2026-10-13: a 5 AM dialysis quote came out at daytime because this
    // field read "for traffic estimate" and "leave blank". The pickup time is
    // what decides overnight (11 PM-7 AM = two transporters) and holiday
    // pricing, so a quote without it is wrong for anyone outside 7 AM-11 PM.
    if (!form.departureTime) {
      setError('Enter the pickup date and time. It decides overnight (11 PM to 7 AM) and holiday pricing, so a quote without it can be wrong.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await api.post('/estimate', {
        ...form,
        holidayMultiplier: form.holidayMultiplier,
        departureTime: etToISO(form.departureTime),
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not generate estimate');
    } finally {
      setLoading(false);
    }
  };

  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handlePrint = () => window.print();

  const handleEmail = async () => {
    if (!emailTo || !result) return;
    setEmailSending(true);
    try {
      await api.post('/estimate/email', { to: emailTo, estimate: result, form });
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 3000);
    } catch (err) {
      alert('Could not send email: ' + (err.response?.data?.error || err.message));
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Trip Estimate</h1>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>Private pay cost estimator</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: result ? '1fr 1fr' : '1fr', gap: 20 }}>
        {/* Form */}
        <div>
          <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, padding: '16px 20px' }}>
            <AddressInput
              label="Pickup address"
              value={form.originAddress}
              onChange={v => setForm(f => ({ ...f, originAddress: v }))}
              onPlaceSelect={v => setForm(f => ({ ...f, originAddress: v }))}
              placeholder="Start typing an address..."
            />
            <AddressInput
              label="Destination address"
              value={form.destinationAddress}
              onChange={v => setForm(f => ({ ...f, destinationAddress: v }))}
              onPlaceSelect={v => setForm(f => ({ ...f, destinationAddress: v }))}
              placeholder="Start typing an address..."
            />

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Pickup date and time <span style={{ color: '#A32D2D' }}>(required: sets overnight and holiday pricing)</span></label>
              <input type="datetime-local" value={form.departureTime}
                onChange={e => setForm(f => ({ ...f, departureTime: e.target.value }))}
                style={{ width: '100%' }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#333', marginTop: 6 }}>
                <input type="checkbox" checked={!!form.timeUnknown} onChange={e => setForm(f => ({ ...f, timeUnknown: e.target.checked }))} />
                Pickup time not known yet: also show the price if it lands overnight (11 PM to 6:59 AM)
              </label>
              <div style={{ fontSize: 10, color: '#999', marginTop: 3 }}>
                Pickups between 11:00 PM and 6:59 AM price at two transporters. Holidays price at the holiday rate. Traffic is estimated for this time too.
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Vehicle type</label>
              <select value={form.vehicleType} onChange={e => setForm(f => ({ ...f, vehicleType: e.target.value }))} style={{ width: '100%' }}>
                <option value="wheelchair_van">Wheelchair van</option>
                <option value="sedan">Sedan</option>
              </select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Number of transporters</label>
              <select value={form.numTransporters} onChange={e => setForm(f => ({ ...f, numTransporters: Number(e.target.value) }))} style={{ width: '100%' }}>
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div onClick={() => setForm(f => ({ ...f, roundTrip: !f.roundTrip }))}
                style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', background: form.roundTrip ? '#185FA5' : '#ddd', position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.roundTrip ? 18 : 2, transition: 'left 0.15s' }} />
              </div>
              <span style={{ fontSize: 13 }}>Round trip (billed as 2 separate trips)</span>
            </div>

            {form.roundTrip && (
              <div style={{ marginBottom: 12, paddingLeft: 8, borderLeft: '2px solid #eee' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div onClick={() => setForm(f => ({ ...f, driverStays: !f.driverStays }))}
                    style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', background: form.driverStays ? '#185FA5' : '#ddd', position: 'relative', flexShrink: 0 }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.driverStays ? 18 : 2, transition: 'left 0.15s' }} />
                  </div>
                  <span style={{ fontSize: 13 }}>Driver stays &amp; waits (one continuous booking, mileage once)</span>
                </div>
                {form.driverStays && (
                  <div>
                    <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Appointment length (minutes)</label>
                    <input type="number" min="0" step="15" value={form.waitMinutes}
                      onChange={e => setForm(f => ({ ...f, waitMinutes: Number(e.target.value) || 0 }))}
                      style={{ width: 110 }} />
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>Time beyond the quote bills at the hourly rate — book as open-clock prepay.</div>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Holiday rate</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  { value: 1, label: 'Standard' },
                  { value: 1.5, label: '1.5x Holiday' },
                  { value: 2, label: '2x Holiday' },
                ].map(opt => (
                  <button key={opt.value}
                    onClick={() => setForm(f => ({ ...f, holidayMultiplier: opt.value }))}
                    style={{
                      flex: 1, fontSize: 12, padding: '8px 6px', borderRadius: 8,
                      background: form.holidayMultiplier === opt.value ? '#185FA5' : 'white',
                      color: form.holidayMultiplier === opt.value ? 'white' : '#666',
                      border: `0.5px solid ${form.holidayMultiplier === opt.value ? '#185FA5' : '#e0e0db'}`,
                      fontWeight: form.holidayMultiplier === opt.value ? 500 : 400,
                    }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
                {error}
              </div>
            )}

            <button onClick={handleEstimate} disabled={loading}
              style={{ width: '100%', fontSize: 14, padding: '12px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, fontWeight: 500 }}>
              {loading ? 'Calculating...' : 'Get estimate'}
            </button>
          </div>
        </div>

        {/* Result */}
        {result && (
          <div id="estimate-result">
            <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, padding: '20px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Commonwealth Transport — Trip Estimate
              </div>

              {result.leg1 && Number(result.leg1.overnightMultiplier) > 1 && (
                <div style={{ display: 'inline-block', background: '#EEEDFE', color: '#3C3489', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                  Overnight pickup (11 PM to 7 AM): priced at two transporters
                </div>
              )}
              {result.leg1 && Number(result.leg1.holidayMultiplier) > 1 && (
                <div style={{ display: 'inline-block', background: '#FFF7E6', color: '#854F0B', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, marginBottom: 6, marginLeft: 6 }}>
                  Holiday rate{result.leg1.holidayName ? ': ' + result.leg1.holidayName : ''}
                </div>
              )}
              <div style={{ fontSize: 28, fontWeight: 500, color: '#185FA5', marginBottom: result.overnightAlternative ? 4 : 16 }}>
                ${result.grandTotal.toFixed(2)}{result.overnightAlternative ? <span style={{ fontSize: 13, color: '#666', marginLeft: 8 }}>daytime pickup</span> : null}
              </div>
              {result.overnightAlternative && (
                <div style={{ background: '#FFF7E6', border: '0.5px solid #F0C36D', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: '#854F0B' }}>
                  <strong>${Number(result.overnightAlternative.grandTotal).toFixed(2)}</strong> if the pickup ends up between 11:00 PM and 6:59 AM (two transporters required). Quote the caller both numbers.
                </div>
              )}

              <div style={{ borderTop: '0.5px solid #f0f0f0', paddingTop: 12 }}>
                <div style={{ fontSize: 11, color: '#999', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Route</div>
                <div style={{ fontSize: 12, marginBottom: 4 }}><strong>From:</strong> {result.route.origin}</div>
                <div style={{ fontSize: 12, marginBottom: 4 }}><strong>To:</strong> {result.route.destination}</div>
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                  <strong>Distance:</strong> {result.route.oneWayMiles} mi each way ({result.route.roundTripMiles} mi round trip)
                </div>
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                  <strong>Drive time:</strong> ~{result.route.oneWayMinutes} min each way
                </div>
                <div style={{ fontSize: 12 }}>
                  <strong>Total time:</strong> ~{result.route.totalMinutes} min <span style={{ fontSize: 10, color: '#999' }}>(includes 20 min load/unload)</span>
                </div>
              </div>

              {/* Leg 1 */}
              <div style={{ borderTop: '0.5px solid #f0f0f0', paddingTop: 12, marginTop: 12 }}>
                <div style={{ fontSize: 11, color: '#999', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {result.driverStays ? 'Door-to-door — driver stays' : result.route.roundTrip ? 'Leg 1 — Outbound' : 'Trip breakdown'}
                </div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                  {result.leg1.billableHours} billable hr{result.leg1.billableHours !== 1 ? 's' : ''}
                </div>
                {result.leg1.breakdown.map((line, i) => (
                  <div key={i} style={{ fontSize: 12, marginBottom: 4, color: '#444' }}>{line}</div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 500, marginTop: 6 }}>
                  <span>{result.driverStays ? 'Total' : result.route.roundTrip ? 'Leg 1 subtotal' : 'Total'}</span>
                  <span>${result.leg1.total.toFixed(2)}</span>
                </div>
                {result.quoteNote && (
                  <div style={{ fontSize: 12, color: '#B26A00', background: '#FFF8E1', borderRadius: 6, padding: '8px 10px', marginTop: 8 }}>
                    {result.quoteNote}
                  </div>
                )}
              </div>

              {/* Leg 2 (return) */}
              {result.leg2 && (
                <div style={{ borderTop: '0.5px solid #f0f0f0', paddingTop: 12, marginTop: 12 }}>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Leg 2 — Return
                  </div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                    {result.leg2.billableHours} billable hr{result.leg2.billableHours !== 1 ? 's' : ''}
                  </div>
                  {result.leg2.breakdown.map((line, i) => (
                    <div key={i} style={{ fontSize: 12, marginBottom: 4, color: '#444' }}>{line}</div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 500, marginTop: 6 }}>
                    <span>Leg 2 subtotal</span>
                    <span>${result.leg2.total.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Tolls */}
              {result.tollEstimate > 0 && (
                <div style={{ borderTop: '0.5px solid #f0f0f0', paddingTop: 12, marginTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span>Estimated EZ-Pass tolls (round trip)</span>
                    <span style={{ fontWeight: 500 }}>${result.tollEstimate.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Grand total */}
              {result.route.roundTrip && (
                <div style={{ borderTop: '2px solid #185FA5', paddingTop: 10, marginTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 600, color: '#185FA5' }}>
                  <span>{result.driverStays ? 'Total (driver stays)' : 'Total (2 trips)'}</span>
                  <span>${result.grandTotal.toFixed(2)}</span>
                </div>
              )}

              <div style={{ borderTop: '0.5px solid #f0f0f0', paddingTop: 12, marginTop: 12 }}>
                <div style={{ fontSize: 11, color: '#999', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rates</div>
                <div style={{ fontSize: 11, color: '#666' }}>
                  Hourly: ${result.rates.hourlyRate}/hr per transporter · 1 hr minimum per trip
                </div>
                <div style={{ fontSize: 11, color: '#666' }}>
                  Mileage: ${result.rates.sedanMileageRate}/mi (sedan) · ${result.rates.wheelchairMileageRate}/mi (WC)
                </div>
              </div>

              {result.leg1.holidayMultiplier > 1 && (
                <div style={{ background: '#FAEEDA', borderRadius: 8, padding: '8px 12px', marginTop: 12, fontSize: 12, color: '#854F0B' }}>
                  Holiday rate applied: {result.leg1.holidayMultiplier}x
                </div>
              )}

              <div style={{ borderTop: '0.5px solid #f0f0f0', paddingTop: 12, marginTop: 12, fontSize: 11, color: '#999', lineHeight: 1.5 }}>
                This is an estimate only. Any wait time is charged at ${result.rates.hourlyRate}/hr per transporter, rounded to the nearest 15 minutes. Actual charges may vary based on traffic, route conditions, and time on scene. Holiday rates (1.5x or 2x) apply on designated holidays. For will-call return trips, we will arrive as quickly as possible once notified, but brief wait times at pickup may occur depending on vehicle and transporter availability. Cancellation fees may apply.
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)}
                placeholder="recipient@email.com" style={{ flex: 1, fontSize: 12 }} />
              <button onClick={handleEmail} disabled={emailSending || !emailTo}
                style={{ fontSize: 12, padding: '8px 16px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, whiteSpace: 'nowrap' }}>
                {emailSent ? 'Sent!' : emailSending ? 'Sending...' : 'Email estimate'}
              </button>
            </div>
            <button onClick={handlePrint}
              style={{ width: '100%', fontSize: 13, padding: '10px', background: '#f0f0f0', border: '0.5px solid #e0e0db', borderRadius: 8 }}>
              Print estimate
            </button>
          </div>
        )}
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #estimate-result, #estimate-result * { visibility: visible !important; }
          #estimate-result { position: absolute; left: 0; top: 0; width: 100%; }
          .sidebar-desktop, .bottom-nav, nav, button, input, select { display: none !important; }
        }
      `}</style>
    </div>
  );
}
